import { RefObject, useEffect } from 'react';

/**
 * This hook handles clicks outside.
 *
 * @param enabled - whether this hook is enabled or not.
 * @param handler - handler to call when clicked outside.
 * @param refs - either a ref or an array of refs. Clicking those refs would not trigger the handler.
 *               IMPORTANT! - Use memoization when passing an array.
 * @param target - the target element to listen to.
 * @see Menu Overlay Tooltip
 */
export default function useOnClickOutside(
  enabled: boolean,
  handler: (event: MouseEvent) => void,
  refs: RefObject<Element | null> | RefObject<Element | null>[],
  target: Window | Document | HTMLElement | null = document,
) {
  useEffect(() => {
    if (!enabled) return;
    const listener = (event: Event) => {
      const refsArr = Array.isArray(refs) ? refs : [refs];
      // Do nothing if clicking tooltipRef’s element or descendent elements
      if (
        refsArr.some((ref) => ref?.current?.contains(event.target as Element))
      ) {
        return;
      }

      handler(event as MouseEvent);
    };

    target?.addEventListener('mousedown', listener);

    // eslint-disable-next-line consistent-return
    return () => {
      target?.removeEventListener('mousedown', listener);
    };
  }, [enabled, handler, refs, target]);
}
